import React, { useEffect, useState, useMemo } from "react";
import "../styles/dashboard.css";
import {
  Users,
  UserPlus,
  Calendar,
  DollarSign,
  Briefcase,
  TrendingUp,
  Target,
  CheckCircle,
  UserCog,
  Package // Added booking icon
} from "lucide-react";

import StatsCard from "../components/dashboard/StatsCard";
import RecentLeads from "../components/dashboard/RecentLeads";
import RecentClients from "../components/dashboard/RecentClients";
import RevenueChart from "../components/dashboard/RevenueChart";
import LeadsBySource from "../components/dashboard/LeadsBySource";
import ConversionRate from "../components/dashboard/ConversionRate";

/* ================= API FUNCTIONS ================= */

const API_BASE = "https://crmapi.zauntrip.com";

// Fetch leads from API
const fetchLeads = async () => {
  try {
    const res = await fetch(`${API_BASE}/api/leads`);
    if (!res.ok) throw new Error("Failed to fetch leads");
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("Error fetching leads:", error);
    return [];
  }
};

// Fetch clients from API
const fetchClients = async () => {
  try {
    const res = await fetch(`${API_BASE}/api/clients`);
    if (!res.ok) throw new Error("Failed to fetch clients");
    const data = await res.json();
    // Handle both response formats
    if (data.success && Array.isArray(data.data)) {
      return data.data;
    } else if (Array.isArray(data)) {
      return data;
    }
    return [];
  } catch (error) {
    console.error("Error fetching clients:", error);
    return [];
  }
};

// Fetch agents from API
const fetchAgents = async () => {
  try {
    const res = await fetch(`${API_BASE}/api/agents`);
    if (!res.ok) throw new Error("Failed to fetch agents");
    const data = await res.json();
    if (data.success && Array.isArray(data.data)) {
      return data.data;
    }
    return [];
  } catch (error) {
    console.error("Error fetching agents:", error);
    return [];
  }
};

// Fetch bookings from API
const fetchBookings = async () => {
  try {
    const res = await fetch(`${API_BASE}/api/bookings`);
    if (!res.ok) {
      const errorText = await res.text();
      console.error("Failed to fetch bookings:", res.status, errorText);
      throw new Error(`Failed to fetch bookings: ${res.status}`);
    }
    const data = await res.json();
    console.log("Bookings API response:", data);
    
    if (data.success && Array.isArray(data.data)) {
      return data.data;
    } else if (Array.isArray(data)) {
      return data;
    } else if (data.success && data.data && Array.isArray(data.data.data)) {
      // Handle nested data structure
      return data.data.data;
    }
    console.warn("Unexpected bookings response format:", data);
    return [];
  } catch (error) {
    console.error("Error fetching bookings:", error);
    return [];
  }
};

/* ================= DASHBOARD ================= */

export default function Dashboard() {
  const [user, setUser] = useState(null);
  const [leads, setLeads] = useState([]);
  const [clients, setClients] = useState([]);
  const [agents, setAgents] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);

  // Memoized calculations for bookings
  const thisMonthBookings = useMemo(() => {
    if (!bookings.length) return [];
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    return bookings.filter(booking => {
      try {
        const bookingDate = booking.createdAt ? new Date(booking.createdAt) : 
                          booking.createdDate ? new Date(booking.createdDate) : 
                          booking.startDate ? new Date(booking.startDate) : null;
        
        if (!bookingDate || isNaN(bookingDate.getTime())) return false;
        
        return (
          bookingDate.getMonth() === currentMonth &&
          bookingDate.getFullYear() === currentYear
        );
      } catch (error) {
        console.error("Error processing booking date:", booking, error);
        return false;
      }
    });
  }, [bookings]);

  const upcomingBookings = useMemo(() => {
    if (!bookings.length) return [];
    
    return bookings
      .filter(booking => {
        try {
          if (!booking.startDate) return false;
          const travelDate = new Date(booking.startDate);
          return !isNaN(travelDate.getTime()) && travelDate > new Date();
        } catch (error) {
          console.error("Error processing upcoming booking:", booking, error);
          return false;
        }
      })
      .sort((a, b) => {
        try {
          const dateA = new Date(a.startDate);
          const dateB = new Date(b.startDate);
          return dateA - dateB;
        } catch (error) {
          return 0;
        }
      })
      .slice(0, 5);
  }, [bookings]);

  const confirmedBookings = useMemo(() => {
    return bookings.filter(booking => 
      booking.bookingStatus === "Confirmed" || 
      booking.status === "Confirmed"
    );
  }, [bookings]);

  const cancelledBookings = useMemo(() => {
    return bookings.filter(booking => 
      booking.bookingStatus === "Cancelled" || 
      booking.status === "Cancelled"
    );
  }, [bookings]);

  const pendingBookings = useMemo(() => {
    return bookings.filter(booking => 
      booking.bookingStatus === "Pending" || 
      booking.status === "Pending"
    );
  }, [bookings]);

  // Revenue calculations
  const thisMonthRevenue = useMemo(() => {
    return thisMonthBookings.reduce((sum, booking) => {
      const amount = booking.totalAmount || booking.total_amount || 0;
      return sum + Number(amount);
    }, 0);
  }, [thisMonthBookings]);

  const totalRevenue = useMemo(() => {
    return bookings.reduce((sum, booking) => {
      const amount = booking.totalAmount || booking.total_amount || 0;
      return sum + Number(amount);
    }, 0);
  }, [bookings]);

  const revenueChangePercentage = useMemo(() => {
    if (bookings.length === 0) return 0;
    
    // Calculate last month's revenue for comparison
    const now = new Date();
    const lastMonth = now.getMonth() - 1;
    const currentYear = now.getFullYear();
    
    const lastMonthBookings = bookings.filter(booking => {
      try {
        const bookingDate = booking.createdAt ? new Date(booking.createdAt) : 
                          booking.createdDate ? new Date(booking.createdDate) : 
                          booking.startDate ? new Date(booking.startDate) : null;
        
        if (!bookingDate || isNaN(bookingDate.getTime())) return false;
        
        return (
          bookingDate.getMonth() === lastMonth &&
          bookingDate.getFullYear() === currentYear
        );
      } catch (error) {
        return false;
      }
    });
    
    const lastMonthRevenue = lastMonthBookings.reduce((sum, booking) => {
      const amount = booking.totalAmount || booking.total_amount || 0;
      return sum + Number(amount);
    }, 0);
    
    if (lastMonthRevenue === 0) return thisMonthRevenue > 0 ? 100 : 0;
    
    return Math.round(((thisMonthRevenue - lastMonthRevenue) / lastMonthRevenue) * 100);
  }, [bookings, thisMonthRevenue]);

  const activeBookings = useMemo(() => {
    return bookings.filter(booking => {
      const status = booking.bookingStatus || booking.status;
      return status === "Confirmed" || status === "In Progress" || status === "Active";
    }).length;
  }, [bookings]);

  // Agent calculations
  const activeAgents = useMemo(() => {
    return agents.filter(agent => agent.isActive !== false);
  }, [agents]);

  const registeredAgents = useMemo(() => {
    return agents.filter(agent => agent.registrationNumber).length;
  }, [agents]);

  // Lead calculations
  const activeLeads = useMemo(() => {
    return leads.filter(lead => 
      !lead.converted_to_customer && lead.status !== "Lost"
    );
  }, [leads]);

  const newLeadsThisWeek = useMemo(() => {
    return leads.filter(lead => {
      if (!lead.createdDate) return false;
      const leadDate = new Date(lead.createdDate);
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return leadDate > weekAgo;
    }).length;
  }, [leads]);

  const conversionRate = useMemo(() => {
    const totalLeads = leads.length;
    return totalLeads > 0 
      ? Math.round((clients.length / (clients.length + activeLeads.length)) * 100) 
      : 0;
  }, [leads.length, clients.length, activeLeads.length]);

  const vipClients = useMemo(() => {
    return clients.filter(client => 
      client.status === "Active" || client.status === "Converted"
    ).length;
  }, [clients]);

  // Payment status calculations
  const paidBookings = useMemo(() => {
    return bookings.filter(booking => 
      booking.paymentStatus === "Paid"
    ).length;
  }, [bookings]);

  const pendingPaymentBookings = useMemo(() => {
    return bookings.filter(booking => 
      booking.paymentStatus === "Pending"
    ).length;
  }, [bookings]);

  const partialPaymentBookings = useMemo(() => {
    return bookings.filter(booking => 
      booking.paymentStatus === "Partial"
    ).length;
  }, [bookings]);

  // Refresh data function
  const refreshData = async () => {
    try {
      setStatsLoading(true);
      const [leadsData, clientsData, agentsData, bookingsData] = await Promise.all([
        fetchLeads(),
        fetchClients(),
        fetchAgents(),
        fetchBookings()
      ]);
      
      setLeads(leadsData);
      setClients(clientsData);
      setAgents(agentsData);
      setBookings(bookingsData);
      
      console.log("Data refreshed successfully");
      console.log("Total bookings:", bookingsData.length);
      console.log("This month bookings:", thisMonthBookings.length);
      console.log("This month revenue:", thisMonthRevenue);
      
    } catch (error) {
      console.error("Error refreshing data:", error);
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    // Mock user (replace later with auth)
    setUser({ full_name: "Admin User" });

    const loadData = async () => {
      try {
        setLoading(true);
        
        // Fetch all data in parallel
        const [leadsData, clientsData, agentsData, bookingsData] = await Promise.all([
          fetchLeads(),
          fetchClients(),
          fetchAgents(),
          fetchBookings()
        ]);
        
        setLeads(leadsData);
        setClients(clientsData);
        setAgents(agentsData);
        setBookings(bookingsData);

        console.log("Dashboard data loaded:");
        console.log("- Leads:", leadsData.length);
        console.log("- Clients:", clientsData.length);
        console.log("- Agents:", agentsData.length);
        console.log("- Bookings:", bookingsData.length);

      } catch (err) {
        console.error("Dashboard API error:", err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
    
    // Refresh data every 5 minutes
    const intervalId = setInterval(refreshData, 5 * 60 * 1000);
    
    return () => clearInterval(intervalId);
  }, []);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Get client name from booking
  const getClientName = (booking) => {
    if (booking.clientId && typeof booking.clientId === 'object') {
      return booking.clientId.fullName || booking.clientId.email || "Unknown Client";
    }
    return booking.customer_name || "Unknown Client";
  };

  // Get agent name from booking
  const getAgentName = (booking) => {
    if (booking.agentId && typeof booking.agentId === 'object') {
      const firstName = booking.agentId.firstName || "";
      const lastName = booking.agentId.lastName || "";
      return `${firstName} ${lastName}`.trim() || booking.agentId.email || "No Agent";
    }
    return booking.agent_name || "No Agent";
  };

  /* ---------- UI ---------- */

  if (loading) {
    return (
      <div className="dashboard">
        <div className="dashboard-header">
          <h1>Loading Dashboard...</h1>
          <p>Please wait while we fetch your data</p>
        </div>
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div>
          <h1>
            Welcome back, {user?.full_name?.split(" ")[0] || "Admin"}
          </h1>
          <p>
            Here's what's happening with your travel business today
          </p>
        </div>
        <button 
          className="btn-outline btn-sm"
          onClick={refreshData}
          disabled={statsLoading}
        >
          {statsLoading ? "Refreshing..." : "Refresh Data"}
        </button>
      </div>

      {/* STATS - Updated with Bookings */}
      <div className="stats-grid">
        <StatsCard
          title="Total Bookings"
          value={bookings.length}
          icon={Package}
          trend={`${thisMonthBookings.length} this month`}
          variant="primary"
          loading={statsLoading}
        />

        <StatsCard
          title="Active Agents"
          value={activeAgents.length}
          icon={UserCog}
          trend={`${registeredAgents} registered`}
          variant="accent"
          loading={statsLoading}
        />

        <StatsCard
          title="Active Leads"
          value={activeLeads.length}
          icon={Target}
          trend={`${newLeadsThisWeek} new this week`}
          variant="info"
          loading={statsLoading}
        />

        <StatsCard
          title="Total Revenue"
          value={formatCurrency(totalRevenue)}
          icon={DollarSign}
          trend={`${revenueChangePercentage >= 0 ? '+' : ''}${revenueChangePercentage}% from last month`}
          variant={revenueChangePercentage >= 0 ? "success" : "danger"}
          loading={statsLoading}
        />

        <StatsCard
          title="Active Bookings"
          value={activeBookings}
          icon={Calendar}
          trend={`${confirmedBookings.length} confirmed`}
          variant="warning"
          loading={statsLoading}
        />

        <StatsCard
          title="Conversion Rate"
          value={`${conversionRate}%`}
          icon={TrendingUp}
          trend={`${clients.length} converted from leads`}
          variant="success"
          loading={statsLoading}
        />
      </div>

      {/* ROW 1 - Overview Cards */}
      <div className="dashboard-grid">
        <div className="span-1">
          <div className="card booking-stats-card">
            <h3>Booking Status</h3>
            {statsLoading ? (
              <div className="loading-text">Loading booking stats...</div>
            ) : bookings.length === 0 ? (
              <div className="empty-state">
                <Package size={48} />
                <p>No bookings found</p>
              </div>
            ) : (
              <div className="booking-stats">
                <div className="booking-stat-item">
                  <div className="stat-label">Confirmed</div>
                  <div className="stat-value confirmed">{confirmedBookings.length}</div>
                  <div className="stat-percentage">
                    {bookings.length > 0 ? Math.round((confirmedBookings.length / bookings.length) * 100) : 0}%
                  </div>
                </div>
                <div className="booking-stat-item">
                  <div className="stat-label">Pending</div>
                  <div className="stat-value pending">{pendingBookings.length}</div>
                  <div className="stat-percentage">
                    {bookings.length > 0 ? Math.round((pendingBookings.length / bookings.length) * 100) : 0}%
                  </div>
                </div>
                <div className="booking-stat-item">
                  <div className="stat-label">Cancelled</div>
                  <div className="stat-value cancelled">{cancelledBookings.length}</div>
                  <div className="stat-percentage">
                    {bookings.length > 0 ? Math.round((cancelledBookings.length / bookings.length) * 100) : 0}%
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="span-1">
          <div className="card payment-stats-card">
            <h3>Payment Status</h3>
            {statsLoading ? (
              <div className="loading-text">Loading payment stats...</div>
            ) : bookings.length === 0 ? (
              <div className="empty-state">
                <DollarSign size={48} />
                <p>No payment data</p>
              </div>
            ) : (
              <div className="payment-stats">
                <div className="payment-stat-item">
                  <div className="stat-label">Paid</div>
                  <div className="stat-value paid">{paidBookings}</div>
                  <div className="stat-percentage">
                    {bookings.length > 0 ? Math.round((paidBookings / bookings.length) * 100) : 0}%
                  </div>
                </div>
                <div className="payment-stat-item">
                  <div className="stat-label">Partial</div>
                  <div className="stat-value partial">{partialPaymentBookings}</div>
                  <div className="stat-percentage">
                    {bookings.length > 0 ? Math.round((partialPaymentBookings / bookings.length) * 100) : 0}%
                  </div>
                </div>
                <div className="payment-stat-item">
                  <div className="stat-label">Pending</div>
                  <div className="stat-value pending-payment">{pendingPaymentBookings}</div>
                  <div className="stat-percentage">
                    {bookings.length > 0 ? Math.round((pendingPaymentBookings / bookings.length) * 100) : 0}%
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="span-1">
          <RecentClients clients={clients.slice(0, 4)} loading={statsLoading} />
        </div>
      </div>

      {/* ROW 2 */}
      <div className="dashboard-grid">
        <div className="span-2">
          <RevenueChart bookings={bookings} loading={statsLoading} />
        </div>
        <div className="span-1">
          <RecentLeads leads={leads.slice(0, 5)} loading={statsLoading} />
        </div>
      </div>

      {/* ROW 3 */}
      <div className="dashboard-grid">
        <div className="span-2">
          <div className="card upcoming-bookings">
            <div className="card-header">
              <h3>Upcoming Travel Bookings</h3>
              <span className="badge">{upcomingBookings.length} upcoming</span>
            </div>
            {statsLoading ? (
              <div className="loading-text">Loading upcoming bookings...</div>
            ) : upcomingBookings.length === 0 ? (
              <div className="empty-state">
                <Calendar size={48} />
                <p>No upcoming bookings found</p>
                <a href="/bookings" className="btn-outline">View All Bookings</a>
              </div>
            ) : (
              <div className="upcoming-list">
                {upcomingBookings.map(booking => {
                  const clientName = getClientName(booking);
                  const agentName = getAgentName(booking);
                  const startDate = booking.startDate ? new Date(booking.startDate) : null;
                  const totalAmount = booking.totalAmount || booking.total_amount || 0;
                  const bookingStatus = booking.bookingStatus || booking.status || "Pending";
                  const paymentStatus = booking.paymentStatus || "Pending";
                  
                  return (
                    <div key={booking._id || booking.id} className="upcoming-item">
                      <div className="upcoming-info">
                        <div className="upcoming-header">
                          <span className="customer-name">{clientName}</span>
                          <span className="booking-amount">{formatCurrency(totalAmount)}</span>
                        </div>
                        <div className="upcoming-details">
                          <span className="travel-date">
                            {startDate ? startDate.toLocaleDateString('en-US', {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric'
                            }) : "Date not set"}
                          </span>
                          <span className="agent-name">Agent: {agentName}</span>
                        </div>
                      </div>
                      <div className="booking-status-container">
                        <span className={`booking-status status-${bookingStatus?.toLowerCase()}`}>
                          {bookingStatus}
                        </span>
                        <span className={`payment-status status-${paymentStatus?.toLowerCase()}`}>
                          {paymentStatus}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="card-footer">
              <a href="/bookings" className="btn-link">View All Bookings →</a>
            </div>
          </div>
        </div>

        <div className="span-1">
          <div className="card agent-stats">
            <div className="card-header">
              <h3>Agent Overview</h3>
              <span className="badge">{agents.length} total</span>
            </div>
            {statsLoading ? (
              <div className="loading-text">Loading agent data...</div>
            ) : agents.length === 0 ? (
              <div className="empty-state">
                <UserCog size={48} />
                <p>No agents found</p>
                <a href="/agents" className="btn-outline">Add Agents</a>
              </div>
            ) : (
              <div className="agent-overview">
                <div className="agent-stat-item">
                  <div className="stat-label">Total Agents</div>
                  <div className="stat-value">{agents.length}</div>
                </div>
                <div className="agent-stat-item">
                  <div className="stat-label">Active</div>
                  <div className="stat-value active">{activeAgents.length}</div>
                </div>
                <div className="agent-stat-item">
                  <div className="stat-label">Registered</div>
                  <div className="stat-value registered">{registeredAgents}</div>
                </div>
                <div className="agent-stat-item">
                  <div className="stat-label">Top Agency</div>
                  <div className="stat-value agency">
                    {(() => {
                      const agencies = agents
                        .filter(a => a.travelAgency)
                        .reduce((acc, agent) => {
                          acc[agent.travelAgency] = (acc[agent.travelAgency] || 0) + 1;
                          return acc;
                        }, {});
                      
                      const topAgency = Object.entries(agencies)
                        .sort((a, b) => b[1] - a[1])[0];
                      
                      return topAgency ? topAgency[0] : "N/A";
                    })()}
                  </div>
                </div>
                <div className="agent-actions">
                  <a href="/agents" className="btn-primary">View All Agents</a>
                  <a href="/agents" className="btn-outline">Add New Agent</a>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}